import { memo, useCallback, useEffect, useRef, useState } from "react";

import styles from "./ProgressiveImage.module.css";

export type ProgressiveImageSource = {
  srcSet: string;
  media?: string;
  type?: string;
};

const PLACEHOLDER_RESOLUTION = 64;

function derivePlaceholderSrc(src: string): string {
  if (!src) return "";
  return src.replace(/(\d+)_/, `${PLACEHOLDER_RESOLUTION}_`);
}

export type ProgressiveImageProps = {
  /** High-resolution image URL. */
  src: string;
  alt: string;
  /** Optional responsive <source> entries rendered inside a <picture>. */
  sources?: ProgressiveImageSource[];
  /**
   * Tiny preview image shown while the high-res asset loads.
   * Defaults to the same URL with a 64px resolution prefix.
   */
  placeholderSrc?: string;
  /** Class for the positioned container (must establish a positioning context). */
  className?: string;
  /**
   * Above-the-fold images: load eagerly with high priority and skip the
   * visibility gate so they contribute to LCP immediately.
   */
  priority?: boolean;
  /** How far outside the viewport to start loading non-priority images. */
  rootMargin?: string;
  /** Called when the high-res image fails to load. */
  onError?: (src: string) => void;
};

/**
 * Renders a 64px preview under the high-res image, then crossfades in once
 * decoded. Off-screen images are gated behind an IntersectionObserver so the
 * high-res request is only made when the image nears the viewport.
 *
 * All loading state is owned internally, so swapping the placeholder for the
 * high-res image re-renders only this component — never the parent card or the
 * surrounding list.
 */
export const ProgressiveImage = memo(function ProgressiveImage({
  src,
  alt,
  sources,
  placeholderSrc,
  className,
  priority = false,
  rootMargin = "200px",
  onError,
}: ProgressiveImageProps) {
  const resolvedPlaceholderSrc = placeholderSrc ?? derivePlaceholderSrc(src);
  const containerRef = useRef<HTMLDivElement>(null);
  // Priority images render immediately; others wait until near the viewport.
  const [visible, setVisible] = useState(priority);
  const [highResLoaded, setHighResLoaded] = useState(false);

  useEffect(() => {
    if (visible) return;
    const node = containerRef.current;
    if (!node) return;
    // No IntersectionObserver (e.g. SSR/older browsers): load immediately.
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible, rootMargin]);

  // A cached image can finish loading before React attaches `onLoad`; the
  // callback ref catches that case so it doesn't stay stuck at opacity 0.
  const imgRef = useCallback((node: HTMLImageElement | null) => {
    if (node && node.complete && node.naturalWidth > 0) {
      setHighResLoaded(true);
    }
  }, []);

  const img = (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      className={`${styles.image} ${highResLoaded ? styles.loaded : ""}`}
      loading={priority ? "eager" : "lazy"}
      // Lowercase `fetchpriority` is passed through verbatim by React (and
      // matches the SSR-emitted attribute), avoiding the "React does not
      // recognize the `fetchPriority` prop" warning + hydration attr mismatch.
      {...({
        fetchpriority: priority ? "high" : "auto",
      } as React.ImgHTMLAttributes<HTMLImageElement>)}
      decoding="async"
      onLoad={() => setHighResLoaded(true)}
      onError={() => onError?.(src)}
    />
  );

  return (
    <div ref={containerRef} className={className ?? ""}>
      {resolvedPlaceholderSrc ? (
        <img
          src={resolvedPlaceholderSrc}
          alt=""
          aria-hidden
          className={styles.placeholder}
          decoding="async"
        />
      ) : null}
      {visible &&
        (sources && sources.length > 0 ? (
          <picture>
            {sources.map((source, index) => (
              <source
                key={index}
                srcSet={source.srcSet}
                media={source.media}
                type={source.type}
              />
            ))}
            {img}
          </picture>
        ) : (
          img
        ))}
    </div>
  );
});
